import Duck from 'extensible-duck';
import uuidV4 from 'uuid/v4';
import _xor from 'lodash/xor';
import _get from 'lodash/get';

export default new Duck({
  namespace: 'GEWEB',
  store: 'BULKUPLOAD',
  initialState: {
    jobID: undefined,
    deliveryInitialized: false,
    deliveryIDs: [],
    POIsInitiated: false,
    metaData: undefined,
    rawData: [],
    offerList: [],
    fetchingPromo: false,
    offerConfig: undefined,
    applicableOfferConfig: undefined,
    grouping: {
      isGrouped: false,
      fetchingGroupQuote: false,
      triggeredGrouping: false,
      trialDeliveries: {},
      clusterMappings: [],
    },
  },
  types: [
    'INIT_BULK_UPLOAD_JOB',
    'CREATE_NEW_DELIVERIES',
    'ADD_RAW_DATA',
    'REMOVE_SUCCESSFUL_DELIVERIES',
    'LINK_NEW_DELIVERIES',
    'FETCH_METADATA',
    'UPDATE_METADATA',
    'BULK_EDIT_RAW_DATA',
    'EDIT_RAW_DATA',
    'FETCH_POIS_FOR_RAW_DATA',
    'UPDATE_POIS_FOR_RAW_DATA',
    'FETCH_OFFER_LIST',
    'UPDATE_PROMO_LIST',
    'INPUT_PROMO_CODE',
    'SET_OFFER_CONFIG',
    'TRY_SET_OFFER_CONFIG',
    'SET_APPLICABLE_OFFER_CONFIG',
    'APPLY_APPLICABLE_OFFER_CONFIG',
    'DELETE_DELIVERY',
    'RESTORE_DELIVERY',
    'ADD_DELIVERY_IDS',
    'REMOVE_DELIVERY_IDS',
    'SPLIT_DROPOFF',
    'GROUPING_RESET_STATE',
    'GROUPING_COMPLETE_RESET_STATE',
    'GROUPING_TRIGGER_CLUSTERING_DELIVERIES',
    'GROUPING_FETCH_QUOTE_TRIAL_GROUPING',
    'GROUPING_SET_TRIAL_DELIVERIES',
    'GROUPING_SET_FETCHING_QUOTE',
    'GROUPING_SWAP_DELIVERIES',
  ],
  creators: duck => ({
    initBulkUploadJob: () => ({ type: duck.types.INIT_BULK_UPLOAD_JOB }),
    createNewDeliveries: () => ({ type: duck.types.CREATE_NEW_DELIVERIES }),
    addRawData: rawData => ({ type: duck.types.ADD_RAW_DATA, rawData }),
    deleteDelivery: deliveryID => ({
      type: duck.types.DELETE_DELIVERY,
      deliveryID,
    }),
    restoreDelivery: deliveryID => ({
      type: duck.types.RESTORE_DELIVERY,
      deliveryID,
    }),
    linkNewDeliveries: deliveryData => ({
      type: duck.types.LINK_NEW_DELIVERIES,
      deliveryData,
    }),
    removeSuccessfulDeliveries: successfulDeliveryIDs => ({
      type: duck.types.REMOVE_SUCCESSFUL_DELIVERIES,
      successfulDeliveryIDs,
    }),
    fetchMetaData: () => ({ type: duck.types.FETCH_METADATA }),
    updateMetaData: ({ metaData }) => ({
      type: duck.types.UPDATE_METADATA,
      metaData,
    }),
    bulkEditRawData: rowChangesMap => ({
      type: duck.types.BULK_EDIT_RAW_DATA,
      rowChangesMap,
    }),
    editRawData: (rowID, fieldName, value) => ({
      type: duck.types.EDIT_RAW_DATA,
      rowID,
      fieldName,
      value,
    }),
    fetchPOIsForRawData: () => ({ type: duck.types.FETCH_POIS_FOR_RAW_DATA }),
    updatePOIsForRawData: updatedPOIs => ({
      type: duck.types.UPDATE_POIS_FOR_RAW_DATA,
      updatedPOIs,
    }),
    fetchOfferList: () => ({ type: duck.types.FETCH_OFFER_LIST }),
    updateOfferList: offers => ({ type: duck.types.UPDATE_PROMO_LIST, offers }),
    inputPromoCode: promoCode => ({
      type: duck.types.INPUT_PROMO_CODE,
      promoCode,
    }),
    setOfferConfig: offer => ({ type: duck.types.SET_OFFER_CONFIG, offer }),
    trySetOfferConfig: offer => ({
      type: duck.types.TRY_SET_OFFER_CONFIG,
      offer,
    }),
    setApplicableOfferConfig: offerWithUpdateQuoteAndConfigs => ({
      type: duck.types.SET_APPLICABLE_OFFER_CONFIG,
      offerWithUpdateQuoteAndConfigs,
    }),
    applyApplicableOfferConfig: () => ({
      type: duck.types.APPLY_APPLICABLE_OFFER_CONFIG,
    }),
    addDeliveryIDs: deliveryIDs => ({
      type: duck.types.ADD_DELIVERY_IDS,
      deliveryIDs,
    }),
    removeDeliveryIDs: deliveryIDs => ({
      type: duck.types.REMOVE_DELIVERY_IDS,
      deliveryIDs,
    }),
    splitDropOff: (deliveryID, index) => ({
      type: duck.types.SPLIT_DROPOFF,
      deliveryID,
      index,
    }),
    groupingResetState: () => ({
      type: duck.types.GROUPING_RESET_STATE,
    }),
    groupingCompleteResetState: () => ({
      type: duck.types.GROUPING_COMPLETE_RESET_STATE,
    }),
    groupingTriggerClusteringDeliveries: payload => ({
      type: duck.types.GROUPING_TRIGGER_CLUSTERING_DELIVERIES,
      payload,
    }),
    groupingFetchTrialQuote: () => ({
      type: duck.types.GROUPING_FETCH_QUOTE_TRIAL_GROUPING,
    }),
    groupingSetTrialDeliveries: (trialDeliveries, clusterMappings) => ({
      type: duck.types.GROUPING_SET_TRIAL_DELIVERIES,
      trialDeliveries,
      clusterMappings,
    }),
    groupingSetFetchingQuote: fetchingQuote => ({
      type: duck.types.GROUPING_SET_FETCHING_QUOTE,
      fetchingQuote,
    }),
    groupingSwapDeliveries: isGrouped => ({
      type: duck.types.GROUPING_SWAP_DELIVERIES,
      isGrouped,
    }),
  }),
  reducer: (state, action, duck) => {
    switch (action.type) {
      case duck.types.INIT_BULK_UPLOAD_JOB: {
        const jobID = uuidV4();
        return {
          ...duck.initialState,
          jobID,
          deliveryInitialized: false,
          POIsInitiated: false,
          rawData: [],
        };
      }
      case duck.types.LINK_NEW_DELIVERIES: {
        const { deliveryData } = action;
        if (!deliveryData) return state;
        const deliveryIDs = deliveryData.map(delivery => delivery.deliveryID);
        // link deliveryID to rawData;
        return {
          ...state,
          deliveryInitialized: true,
          deliveryIDs,
        };
      }
      case duck.types.DELETE_DELIVERY: {
        const { deliveryID } = action;
        return {
          ...state,
          deliveryIDs: state.deliveryIDs.filter(dID => dID !== deliveryID),
        };
      }
      case duck.types.RESTORE_DELIVERY: {
        const { deliveryID } = action;
        if (state.deliveryIDs.find(dID => dID === deliveryID)) {
          return state;
        }
        return {
          ...state,
          deliveryIDs: [...state.deliveryIDs, deliveryID],
        };
      }
      case duck.types.REMOVE_SUCCESSFUL_DELIVERIES: {
        const { successfulDeliveryIDs } = action;
        return {
          ...state,
          deliveryIDs: _xor(state.deliveryIDs, successfulDeliveryIDs),
        };
      }
      case duck.types.ADD_RAW_DATA: {
        const { rawData } = action;
        return {
          ...state,
          rawData: rawData,
        };
      }
      case duck.types.UPDATE_METADATA: {
        const { metaData } = action;

        return {
          ...state,
          metaData: { ...metaData },
        };
      }
      case duck.types.BULK_EDIT_RAW_DATA: {
        const { rawData } = state;
        const { rowChangesMap } = action;
        return {
          ...state,
          rawData: rawData.map(row => {
            if (!rowChangesMap[row.id]) {
              return row;
            }
            return {
              ...row,
              ...rowChangesMap[row.id],
            };
          }),
        };
      }
      case duck.types.EDIT_RAW_DATA: {
        const { rawData } = state;
        const { rowID, fieldName, value } = action;
        const index = rawData.findIndex(row => row.id === rowID);
        if (index < 0) {
          return state;
        }
        rawData[index] = {
          ...rawData[index],
          [fieldName]: value,
        };
        return {
          ...state,
          rawData: [...rawData],
        };
      }
      case duck.types.UPDATE_POIS_FOR_RAW_DATA: {
        const { updatedPOIs } = action;
        const { rawData } = state;
        const newRawData = rawData.map(row => {
          const updated = updatedPOIs[row.id];
          if (!updated) {
            return row;
          }
          return {
            ...row,
            ...updated,
          };
        });
        return {
          ...state,
          POIsInitiated: true,
          rawData: newRawData,
        };
      }
      case duck.types.FETCH_OFFER_LIST: {
        return {
          ...state,
          fetchingPromo: true,
        };
      }
      case duck.types.UPDATE_PROMO_LIST: {
        const { offers } = action;
        return {
          ...state,
          fetchingPromo: false,
          offerList: [...offers],
        };
      }
      case duck.types.SET_OFFER_CONFIG: {
        const { offer } = action;
        return {
          ...state,
          fetchingPromo: false,
          offerConfig: offer,
        };
      }
      case duck.types.TRY_SET_OFFER_CONFIG: {
        return {
          ...state,
          fetchingPromo: true,
        };
      }
      case duck.types.SET_APPLICABLE_OFFER_CONFIG: {
        const { offerWithUpdateQuoteAndConfigs } = action;
        return {
          ...state,
          fetchingPromo: false,
          applicableOfferConfig: offerWithUpdateQuoteAndConfigs,
        };
      }
      case duck.types.ADD_DELIVERY_IDS: {
        const { deliveryIDs } = action;
        return {
          ...state,
          deliveryIDs: [...state.deliveryIDs, ...deliveryIDs],
        };
      }
      case duck.types.REMOVE_DELIVERY_IDS: {
        const { deliveryIDs } = action;
        return {
          ...state,
          deliveryIDs: _xor(state.deliveryIDs, deliveryIDs),
        };
      }
      case duck.types.GROUPING_SET_TRIAL_DELIVERIES: {
        const { trialDeliveries, clusterMappings } = action;
        return {
          ...state,
          grouping: {
            ...state.grouping,
            clusterMappings: clusterMappings ? clusterMappings : [],
            trialDeliveries: trialDeliveries ? trialDeliveries : {},
          },
        };
      }
      case duck.types.GROUPING_TRIGGER_CLUSTERING_DELIVERIES: {
        const { payload } = action;
        if (!_get(payload, 'length', 0)) {
          return state;
        }
        return {
          ...state,
          grouping: {
            ...state.grouping,
            fetchingGroupQuote: true,
            triggeredGrouping: true,
          },
        };
      }
      case duck.types.GROUPING_COMPLETE_RESET_STATE: {
        return {
          ...state,
          grouping: {
            isGrouped: false,
            fetchingGroupQuote: false,
            triggeredGrouping: false,
            trialDeliveries: {},
            clusterMappings: [],
          },
        };
      }
      case duck.types.GROUPING_FETCH_QUOTE_TRIAL_GROUPING: {
        return {
          ...state,
          grouping: {
            ...state.grouping,
            fetchingGroupQuote: true,
          },
        };
      }
      case duck.types.GROUPING_SET_FETCHING_QUOTE: {
        const { fetchingQuote } = action;
        return {
          ...state,
          grouping: {
            ...state.grouping,
            fetchingGroupQuote: fetchingQuote,
          },
        };
      }
      case duck.types.GROUPING_SWAP_DELIVERIES: {
        const { isGrouped } = action;
        return {
          ...state,
          grouping: {
            ...state.grouping,
            isGrouped: isGrouped,
          },
        };
      }
      default: {
        return state;
      }
    }
  },
});
